"use client";

import { useState } from "react";

import { Button } from "#ui/button";
import { Icon } from "#ui/icon";
import { cx } from "#ui/style.utils";

// -----------------------------------------------------------------------------

interface CheckCardListItemProps extends Omit<
  React.ComponentPropsWithoutRef<"div">,
  "children"
> {
  slot0: NonNullable<React.ReactNode>;
  slot1: React.ReactNode;
  slot1Fallback?: React.ReactNode;
}

export function CheckCardListItem({
  className,
  slot0,
  slot1,
  slot1Fallback,
}: CheckCardListItemProps) {
  return (
    <div
      className={cx(
        "flex items-center justify-between gap-10px py-12px text-sm",
        className,
      )}
    >
      <span>{slot0}</span>{" "}
      <span className="text-right">
        {slot1 ?? slot1Fallback ?? (
          <span className="text-shade-40" aria-hidden>
            --
          </span>
        )}
      </span>
    </div>
  );
}

// -----------------------------------------------------------------------------

interface CheckCardListProps extends React.ComponentPropsWithoutRef<"div"> {}

export function CheckCardList({ children, className }: CheckCardListProps) {
  return (
    <div className={cx("divide-y divide-shade-10", className)}>{children}</div>
  );
}

// -----------------------------------------------------------------------------

interface CheckCardListExpandableProps<TItem> extends Omit<
  CheckCardListProps,
  "children"
> {
  items: TItem[];
  limit?: number;
  renderItem: (item: TItem, idx: number) => React.JSX.Element;
}

export function CheckCardListExpandable<TItem>({
  items,
  limit = 3,
  renderItem,
  ...props
}: CheckCardListExpandableProps<TItem>) {
  const [expanded, setExpanded] = useState(items.length <= limit);

  const itemsList = expanded ? items : items.slice(0, limit);

  return (
    <>
      <CheckCardList {...props}>{itemsList.map(renderItem)}</CheckCardList>
      <div
        aria-hidden={expanded ? "true" : undefined}
        className={cx(
          "border-t border-t-shade-10 py-12px text-center",
          expanded ? "hidden" : undefined,
        )}
      >
        <Button
          onClick={() => setExpanded(true)}
          variant="link"
          disabled={expanded}
        >
          <span>Show all</span>
          <Icon name="ms-expand-more" />
        </Button>
      </div>
    </>
  );
}
